import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, AlertCircle, ChevronDown, ChevronUp, Rocket, Edit3 } from 'lucide-react';
import theme from '../theme';
import AuthImage from '../components/AuthImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFER_TYPE_LABEL = {
  physical_product: 'Physical Product', digital_product: 'Digital Product',
  service_program: 'Service / Course / Program', company_employee: 'Company / Employee Kickback',
  event_experience: 'Event / Experience', other: 'Other',
};
const MGMT_LABEL = { diy: 'DIY / Self-Managed', self: 'DIY / Self-Managed', creator: 'DIY / Self-Managed', curator: 'Kickback Architect Managed', architect: 'Kickback Architect Managed', broker: 'Kickback Architect Managed' };
const STANDARD_LABEL = { ERC721: 'ERC-721', ERC1155: 'ERC-1155', ERC20: 'ERC-20' };
const humanOffer = v => OFFER_TYPE_LABEL[(v || '').toLowerCase()] || (v || '—');
const humanMgmt = v => MGMT_LABEL[(v || '').toLowerCase()] || (v || '—');
const humanStd = v => (v ? (STANDARD_LABEL[v.toUpperCase()] || v) : '—');

const cardStyle = { background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, padding: 16 };

/**
 * Batch 1C-B1 — Launch Readiness control center.
 *
 * Rebuilt into a single canonical view driven ONLY by
 * GET /api/creator/portfolios/{id}/readiness. Consumes:
 *   readiness.requirements[]     — with requirement_level + state
 *   readiness.deployment         — canonical deployment eligibility
 *   readiness.blockers[]         — required + incomplete requirements
 *
 * Rules enforced:
 *   • One canonical deployment control (Deployment Readiness card).
 *   • Blocker rows NEVER render an active "Deploy Contract" button;
 *     for the deployment requirement, the primary action reflects
 *     readiness.deployment.deployment_cta_state.
 *   • Completed requirement rows are clickable and open an inline
 *     review drawer with Close + Edit (Edit routes to
 *     /creator/portfolios/{id}/edit?section=X — NEVER the wizard).
 */
const LaunchReadinessPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openReview, setOpenReview] = useState(null); // requirement id opened
  const [launching, setLaunching] = useState(false);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/creator/portfolios/${id}/readiness`, auth);
        setReadiness(r.data.readiness);
        const p = await axios.get(`${API}/creator/portfolios/${id}`, auth);
        setPortfolio(p.data.portfolio);
        if (p.data.portfolio?.contract_id) {
          try {
            const d = await axios.get(`${API}/contracts/drafts/${p.data.portfolio.contract_id}`, auth);
            setDraft(d.data.draft || d.data);
          } catch (_) {}
        }
      } finally { setLoading(false); }
    })();
  }, [id, auth]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Loading…</div>;
  if (!readiness) return <div style={{ padding: 60, textAlign: 'center' }}>Kickback not found.</div>;

  const requirements = readiness.requirements || [];
  const required = requirements.filter(r => r.requirement_level === 'required');
  const blockers = required.filter(r => !r.done);
  const completed = required.filter(r => r.done);
  const recommended = requirements.filter(r => r.requirement_level === 'recommended');
  const notApplicable = requirements.filter(r => r.requirement_level === 'not_applicable');
  const dep = readiness.deployment || { deployment_cta_state: 'hidden', deployment_blockers: [] };
  const isCompletedSetup = !!readiness.initial_setup_completed;

  // Canonical DRAFT → LIVE launch. Backend re-validates readiness; this
  // click never sends an on-chain transaction and is idempotent.
  const handleLaunchKickback = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      const r = await axios.post(`${API}/creator/portfolios/${id}/launch-kickback`, {}, auth);
      if (r.data?.portfolio) setPortfolio(r.data.portfolio);
      const r2 = await axios.get(`${API}/creator/portfolios/${id}/readiness`, auth);
      setReadiness(r2.data.readiness);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Launch failed');
      // eslint-disable-next-line no-console
      console.error('Launch failed:', d);
      alert(msg);
    } finally {
      setLaunching(false);
    }
  };

  const readyBadge = readiness.ready_to_launch;
  const launchStatus = readiness.is_live
    ? 'Kickback is LIVE.'
    : !isCompletedSetup
      ? 'Initial setup is incomplete. Complete Steps 1–5 of the Create Kickback wizard.'
      : !readiness.ready_to_launch
        ? `${blockers.length} requirement${blockers.length === 1 ? '' : 's'} still block${blockers.length === 1 ? 's' : ''} launch.`
        : readiness.is_deployed
          ? 'All required setup complete. Deployment complete. Ready to go LIVE.'
          : 'All required setup complete. Deploy the contract on-chain when ready.';

  // Canonical primary action for the Deployment Readiness card, derived
  // from readiness.deployment.deployment_cta_state + blocker codes.
  const primaryDeployAction = (() => {
    if (dep.deployment_cta_state === 'hidden') {
      return { label: 'Configure Contract', route: `/creator/portfolios/${id}/edit?section=contract`, disabled: false };
    }
    if (dep.deployment_cta_state === 'enabled') {
      return { label: 'Deploy Contract', route: `/creator/contracts/${portfolio?.contract_id}/deploy`, disabled: false, testid: 'deployment-primary-deploy' };
    }
    // disabled — pick the most helpful blocker resolution route
    const b = dep.deployment_blockers?.[0];
    if (!b) return { label: 'Deploy Contract', route: null, disabled: true };
    if (b.code === 'already_deployed') {
      return { label: 'View Deployment', route: `/creator/contracts/${portfolio?.contract_id}/deploy`, disabled: false, testid: 'deployment-view-deployment' };
    }
    if (b.code === 'contract_code_missing' || b.code === 'contract_config_incomplete') {
      return { label: 'Prepare Contract', route: b.resolution_route || `/creator/contracts/${portfolio?.contract_id}`, disabled: false, testid: 'deployment-prepare-contract' };
    }
    if (b.code === 'initial_setup_incomplete') {
      return { label: 'Resume Setup', route: b.resolution_route || `/creator/portfolios/new?edit=${id}&step=5`, disabled: false, testid: 'deployment-resume-setup' };
    }
    return { label: b.label || 'Resolve Blocker', route: b.resolution_route, disabled: !b.resolution_route };
  })();

  const openReviewFor = r => setOpenReview(prev => (prev === r.id ? null : r.id));
  const editRouteFor = section => `/creator/portfolios/${id}/edit?section=${section}`;

  return (
    <div data-testid="launch-readiness-page" style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>
      <button onClick={() => navigate(`/creator/portfolios/${id}`)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 14, fontWeight: 600, marginBottom: 20 }}>
        <ArrowLeft size={16} /> Back to Kickback
      </button>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 4 }}>Launch Readiness</h1>
          <p style={{ fontSize: 14, color: theme.colors.text.secondary }}>{readiness.kickback_name || 'Kickback'}</p>
        </div>
        <div data-testid="readiness-badge" style={{
          padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
          background: readiness.is_live ? `${theme.colors.status.success}22` : (readyBadge ? `${theme.colors.status.success}22` : '#FDF3E7'),
          color: readiness.is_live ? theme.colors.status.success : (readyBadge ? theme.colors.status.success : '#8B5A00'),
        }}>
          {readiness.is_live ? 'Live' : (readyBadge ? 'Ready' : 'Not Ready')}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Metric label="Progress" value={`${readiness.percent}%`} testid="readiness-percent" />
        <Metric label="Required Complete" value={`${readiness.completed_required_count} / ${readiness.required_count}`} testid="readiness-counts" />
        <Metric label="Status" value={readiness.status || '—'} testid="readiness-status" />
        <Metric label="Blockers" value={blockers.length} testid="readiness-blockers-count" />
      </div>

      <div data-testid="launch-status-panel" style={{ padding: 14, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, marginBottom: 20, fontSize: 13, color: theme.colors.text.primary, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div><strong>Launch Status:</strong> {launchStatus}</div>
        {readiness.ready_to_launch && !readiness.is_live && readiness.is_deployed && (
          <button data-testid="launch-kickback-btn" onClick={handleLaunchKickback} disabled={launching}
            style={{ padding: '10px 18px', background: launching ? theme.colors.bg.tertiary : `linear-gradient(135deg, ${theme.colors.status.success}, #06B6D4)`, color: launching ? theme.colors.text.muted : '#FFF', border: 'none', borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 800, letterSpacing: 0.3, cursor: launching ? 'not-allowed' : 'pointer', boxShadow: launching ? 'none' : '0 2px 6px rgba(16,185,129,0.25)' }}>
            <Rocket size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {launching ? 'Launching…' : 'Launch Kickback'}
          </button>
        )}
        {readiness.is_live && (
          <span data-testid="launch-live-badge" style={{ padding: '6px 12px', borderRadius: 999, background: `${theme.colors.status.success}22`, color: theme.colors.status.success, fontSize: 12, fontWeight: 700 }}>LIVE</span>
        )}
      </div>

      {/* A. Deployment Readiness — ONE authoritative control */}
      <DeploymentReadinessPanel
        readiness={readiness} portfolio={portfolio} draft={draft} dep={dep}
        primary={primaryDeployAction} navigate={navigate}
      />

      {/* B. Blocking Launch */}
      {blockers.length > 0 && (
        <section data-testid="blockers-section" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 10 }}>What is blocking launch?</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {blockers.map(b => {
              // Batch 1C-B1 PRE-DEPLOYMENT INVARIANT 2 —
              // the Deployment Readiness panel owns the SINGLE canonical
              // primary deployment action. The blocker row for the
              // `deployment` requirement must NOT render a duplicate
              // clickable button; it only surfaces status / reason and
              // points the user to the Deployment Readiness panel above.
              const isDeployment = b.id === 'deployment';
              const cta = isDeployment ? null : {
                label: b.cta_label, route: b.cta_route, disabled: !!b.blocked, testid: `blocker-${b.id}-cta`,
              };
              return (
                <div key={b.id} data-testid={`blocker-${b.id}`}
                  style={{ padding: 14, background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: theme.radius.md, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <AlertCircle size={18} style={{ color: '#B91C1C', marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#7F1D1D', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {b.label}
                      <span data-testid={`blocker-${b.id}-level`} style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 999, background: '#FEE2E2', color: '#7F1D1D' }}>{b.requirement_level || 'required'}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#7F1D1D', opacity: 0.85, marginBottom: 4 }}>{b.description}</div>
                    <div style={{ fontSize: 12, color: '#7F1D1D', marginBottom: 8 }}>
                      <strong>Current:</strong> {b.current_state || '—'}<br/>
                      <strong>Missing:</strong> {b.missing_reason}
                    </div>
                    {isDeployment ? (
                      <span data-testid="blocker-deployment-refer-panel" style={{ fontSize: 11, color: '#7F1D1D', fontStyle: 'italic' }}>
                        Use the Deployment Readiness panel above to resolve this.
                      </span>
                    ) : cta?.route && !cta.disabled ? (
                      <button data-testid={cta.testid || `blocker-${b.id}-cta`}
                        onClick={() => navigate(cta.route)}
                        style={{ padding: '6px 12px', border: 'none', background: '#DC2626', color: '#FFF', borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        {cta.label || 'Fix'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: '#7F1D1D', fontStyle: 'italic' }}>Waiting on prerequisites.</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* C. Completed Requirements — clickable */}
      {completed.length > 0 && (
        <section data-testid="completed-section" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 10 }}>Completed Requirements</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {completed.map(c => (
              <div key={c.id} data-testid={`completed-${c.id}`}
                style={{ background: `${theme.colors.status.success}12`, border: `1px solid ${theme.colors.status.success}55`, borderRadius: theme.radius.sm }}>
                <button data-testid={`completed-${c.id}-toggle`}
                  onClick={() => openReviewFor(c)}
                  style={{ width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Check size={14} style={{ color: theme.colors.status.success }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>{c.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: theme.colors.text.muted }}>{c.current_state}</span>
                  {openReview === c.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {openReview === c.id && (
                  <CompletedReview requirement={c} portfolio={portfolio} draft={draft}
                    onClose={() => setOpenReview(null)}
                    onEdit={() => navigate(sectionRouteFor(c.id, id))} />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* D. Recommended items */}
      {recommended.length > 0 && (
        <section data-testid="recommended-section" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 8 }}>Recommended</h2>
          {recommended.map(r => (
            <div key={r.id} data-testid={`recommended-${r.id}`} style={{ padding: 10, marginBottom: 6, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: theme.radius.sm, fontSize: 12, color: '#78350F' }}>
              <strong>{r.label}</strong> — {r.description || r.missing_reason}
            </div>
          ))}
        </section>
      )}

      {/* E. Not applicable (collapsed by default) */}
      {notApplicable.length > 0 && (
        <details data-testid="not-applicable-section" style={{ marginBottom: 20 }}>
          <summary style={{ fontSize: 13, color: theme.colors.text.muted, cursor: 'pointer' }}>Not applicable ({notApplicable.length})</summary>
          <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: theme.colors.text.muted }}>
            {notApplicable.map(r => <li key={r.id}>{r.label}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
};

function sectionRouteFor(reqId, portfolioId) {
  const map = { details: 'overview', evidence: 'evidence', artwork: 'artwork', contract: 'contract', management: 'management', review_save: 'launch' };
  const section = map[reqId] || 'overview';
  return `/creator/portfolios/${portfolioId}/edit?section=${section}`;
}

function DeploymentReadinessPanel({ readiness, portfolio, draft, dep, primary, navigate }) {
  const status = (() => {
    if (dep.deployment_cta_state === 'hidden') return 'Not Available';
    if (readiness.is_deployed) return 'Deployed';
    if (dep.deployment_cta_state === 'enabled') return 'Ready to Deploy';
    return 'Blocked';
  })();
  const statusColor = status === 'Deployed' ? theme.colors.status.success : status === 'Ready to Deploy' ? theme.colors.status.info : status === 'Blocked' ? '#B45309' : theme.colors.text.muted;
  return (
    <section data-testid="deployment-readiness-panel" style={{ ...cardStyle, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Rocket size={18} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text.primary, display: 'flex', alignItems: 'center', gap: 8 }}>
              Deployment Readiness
              <span data-testid="network-mode-badge" style={{
                padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                letterSpacing: 0.6, textTransform: 'uppercase',
                background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B',
              }}>Testnet</span>
            </div>
            <div data-testid="deployment-status-label" style={{ fontSize: 12, color: statusColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{status}</div>
          </div>
        </div>
        {primary.route && (
          <button data-testid={primary.testid || 'deployment-primary-cta'}
            disabled={primary.disabled}
            onClick={() => primary.route && navigate(primary.route)}
            style={{
              padding: '8px 14px', borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 700,
              border: 'none', cursor: primary.disabled ? 'not-allowed' : 'pointer',
              background: primary.disabled ? theme.colors.bg.tertiary : theme.colors.primary,
              color: primary.disabled ? theme.colors.text.muted : '#FFF',
              opacity: primary.disabled ? 0.6 : 1,
            }}>
            {primary.label}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
        <MetaRow k="Contract" v={humanStd(draft?.standard)} testid="dep-meta-contract" />
        <MetaRow k="Collection Name" v={(draft?.params?.name || draft?.params?.token_name) || '—'} testid="dep-meta-collection-name" />
        <MetaRow k="Token Symbol" v={draft?.params?.symbol || '—'} testid="dep-meta-token-symbol" />
        <MetaRow
          k="Canonical Total Supply"
          v={(() => {
            const p = draft?.params || {};
            const t = Number(p.total_supply)
              || (Number(p.token_ids || 0) * Number(p.max_supply_per_id || 0))
              || Number(p.max_supply || 0) || 0;
            return t ? t.toLocaleString() : (portfolio?.quantity ? Number(portfolio.quantity).toLocaleString() : '—');
          })()}
          testid="dep-meta-total-supply"
        />
        <MetaRow
          k="Secondary-Sale Royalty"
          v={(() => {
            const r = draft?.params?.royalty_percent;
            return (r !== undefined && r !== null && r !== '') ? `${r}%` : '—';
          })()}
          testid="dep-meta-royalty"
        />
        <MetaRow
          k="Primary-Sale Kickback Contribution"
          v={portfolio?.kickback_percent !== undefined && portfolio?.kickback_percent !== null ? `${portfolio.kickback_percent}%` : '—'}
          testid="dep-meta-primary-contribution"
        />
        <MetaRow k="Draft" v={draft ? 'Created' : 'Missing'} testid="dep-meta-draft" />
        <MetaRow k="Contract Code" v={draft?.generated_code ? 'Generated' : 'Missing'} testid="dep-meta-code" />
        <MetaRow k="Initial Setup" v={readiness.initial_setup_completed ? 'Complete' : 'Incomplete'} testid="dep-meta-setup" />
        <MetaRow k="Network" v="Polygon Amoy (TEST)" testid="dep-meta-network" />
        <MetaRow k="Deployment" v={readiness.is_deployed ? 'Deployed' : 'Not Deployed'} testid="dep-meta-deploy" />
      </div>
      {dep.deployment_blockers && dep.deployment_blockers.length > 0 && (() => {
        const infoOnly = dep.deployment_blockers.every(b => b.severity === 'info' || b.code === 'already_deployed');
        const heading = infoOnly ? 'Status' : 'Blockers';
        const borderColor = infoOnly ? theme.colors.status.success : '#7F1D1D';
        const bg = infoOnly ? `${theme.colors.status.success}10` : 'transparent';
        return (
          <div style={{ background: bg, border: infoOnly ? `1px solid ${theme.colors.status.success}55` : 'none', borderRadius: theme.radius.sm, padding: infoOnly ? '8px 10px' : 0 }}>
            <div data-testid="deployment-status-heading" style={{ fontSize: 12, fontWeight: 700, color: borderColor, marginBottom: 6 }}>{heading}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: borderColor }}>
              {dep.deployment_blockers.map(b => (
                <li key={b.code} data-testid={`deployment-blocker-${b.code}`}>{b.label}</li>
              ))}
            </ul>
          </div>
        );
      })()}
    </section>
  );
}

function MetaRow({ k, v, testid }) {
  return (
    <div data-testid={testid} style={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, padding: '8px 10px' }}>
      <div style={{ fontSize: 10, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>{k}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text.primary }}>{v || '—'}</div>
    </div>
  );
}

function CompletedReview({ requirement, portfolio, draft, onClose, onEdit }) {
  if (!portfolio) return null;
  const rows = (() => {
    switch (requirement.id) {
      case 'details': return [
        ['Name', portfolio.name], ['Description', portfolio.description], ['Category', portfolio.category],
        ['Kickback Types', (portfolio.portfolio_type_ids || []).join(', ') || portfolio.portfolio_type],
        ['Quantity', portfolio.quantity], ['Unit Price', portfolio.unit_price],
      ];
      case 'evidence': {
        const ev = portfolio.offer_evidence || {}; const uploads = ev.uploads || {};
        return [
          ['Offer Type', humanOffer(ev.offer_type)],
          ['Product / Offer Images', (uploads.product_offer_images || []).length],
          ['Unique Markings / Proof Images', (uploads.unique_markings_images || []).length],
          ['Design / Diagram Images', (uploads.design_placement_images || []).length],
          ['Supporting Documents', (uploads.supporting_documents || []).length],
          ['Last Updated', portfolio.updated_at],
        ];
      }
      case 'artwork': return [
        ['Selected artwork count', (portfolio.nft_asset_ids || []).length],
        ['__thumbnails__', portfolio.nft_asset_ids || []],
      ];
      case 'contract': {
        const p = draft?.params || {};
        return [
          ['Contract Standard', humanStd(draft?.standard)], ['Token Name', p.token_name || p.name || p.contract_name],
          ['Token Symbol', p.token_symbol || p.symbol], ['Maximum Supply', p.max_supply],
          ['Draft Status', draft?.deployed ? 'Deployed' : (draft?.status || 'DRAFT')], ['Generated Code', draft?.generated_code ? 'Ready' : 'Missing'],
        ];
      }
      case 'management': return [
        ['Management Type', humanMgmt(portfolio.management_type)],
        ['Kickback Percentage', `${portfolio.kickback_percent || 0}% Kickback`],
        ['Quantity', portfolio.quantity], ['Unit Price', portfolio.unit_price],
      ];
      case 'review_save': return [
        ['Agreement', portfolio.agreement_accepted ? 'Accepted' : '—'],
        ['Accepted At', portfolio.agreement_accepted_at || '—'],
        ['Initial Setup Completed At', portfolio.initial_setup_completed_at || '—'],
      ];
      case 'deployment': return [
        ['Deployment', requirement.current_state],
        ['Deployed Address', draft?.deployed_address || '—'],
        ['Deployed Chain', draft?.deployed_chain || '—'],
      ];
      default: return [];
    }
  })();
  return (
    <div data-testid={`completed-${requirement.id}-review`} style={{ padding: '10px 14px 14px', borderTop: `1px solid ${theme.colors.status.success}55` }}>
      <dl style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '6px 12px', fontSize: 12, margin: 0 }}>
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            {k === '__thumbnails__' ? (
              <>
                <dt style={{ color: theme.colors.text.muted, fontWeight: 500 }}>Thumbnails</dt>
                <dd style={{ margin: 0 }}>
                  {(v || []).length === 0 ? <span style={{ color: theme.colors.text.muted }}>No artwork selected.</span> : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {v.map(id => (
                        <ArtworkThumb key={id} assetId={id} />
                      ))}
                    </div>
                  )}
                </dd>
              </>
            ) : (
              <>
                <dt style={{ color: theme.colors.text.muted, fontWeight: 500 }}>{k}</dt>
                <dd style={{ color: theme.colors.text.primary, margin: 0, wordBreak: 'break-word' }}>{v ?? '—'}</dd>
              </>
            )}
          </React.Fragment>
        ))}
      </dl>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button data-testid={`completed-${requirement.id}-close`} onClick={onClose} style={{ padding: '6px 12px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
        <button data-testid={`completed-${requirement.id}-edit`} onClick={onEdit} style={{ padding: '6px 12px', border: 'none', background: theme.colors.primary, color: '#FFF', borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Edit3 size={12} /> Edit</button>
      </div>
    </div>
  );
}

const Metric = ({ label, value, testid }) => (
  <div data-testid={testid} style={{ padding: 12, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md }}>
    <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text.primary }}>{value}</div>
  </div>
);

// Batch 1C-B1 FINAL Part 2 — visual thumbnail for each selected artwork
// asset. AuthImage handles authenticated fetch + graceful "Preview
// unavailable" fallback. Backend already excludes non-artwork kinds.
function ArtworkThumb({ assetId }) {
  return (
    <div data-testid={`artwork-thumb-${assetId}`} style={{ width: 84, height: 84, borderRadius: 6, overflow: 'hidden', border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.secondary }}>
      <AuthImage url={`/api/nft/file/${assetId}`} alt="Artwork" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        onErrorFallback={<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: theme.colors.text.muted, textAlign: 'center', padding: 4 }}>Preview unavailable</div>} />
    </div>
  );
}

export default LaunchReadinessPage;
